import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Hash, Trophy, Copy, RefreshCcw } from "lucide-react";
import Layout from "../../components/Layout";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function ChallengeAdminView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [codes, setCodes] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ rows: [], status: "" });

  const load = async () => {
    try {
      const [{ data: ch }, { data: cd }, { data: lb }] = await Promise.all([
        api.get(`/challenges/${id}`),
        api.get(`/challenges/${id}/codes`),
        api.get(`/challenges/${id}/leaderboard`),
      ]);
      setChallenge(ch);
      setCodes(cd);
      setLeaderboard(lb);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copié");
    } catch {}
  };

  if (!challenge) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-16 text-white/50 font-mono-stat uppercase">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-white/60 hover:text-cyan-300 mb-6 text-sm font-mono-stat uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] font-mono-stat text-cyan-300">
              Défi · {challenge.type === "series" ? "Série" : "Quiz unique"}
            </span>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mt-1">
              {challenge.title}
            </h1>
            <div className="text-xs text-white/50 mt-1 font-mono-stat">
              {new Date(challenge.starts_at).toLocaleString("fr-FR")} → {new Date(challenge.ends_at).toLocaleString("fr-FR")}
            </div>
          </div>
          <div className="flex items-center gap-2">
          {challenge.access_mode === "qualified_only" && (
            <button
              data-testid="admin-view-quals-cta"
              onClick={() => navigate(`/admin/challenges/${id}/qualifications`)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/40 text-amber-200 transition-all text-sm font-display uppercase tracking-wider"
            >
              🏆 Qualifiés
            </button>
          )}
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all text-sm font-display uppercase tracking-wider"
          >
            <RefreshCcw className="w-4 h-4" /> Actualiser
          </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Codes */}
          <div className="panel rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Hash className="w-5 h-5 text-cyan-300" />
                <span className="font-display font-bold text-lg">Codes achetés</span>
              </div>
              <span className="text-xs font-mono-stat text-white/40">{codes.length}</span>
            </div>
            {codes.length === 0 ? (
              <div className="py-8 text-center text-white/40 text-sm font-mono-stat uppercase tracking-widest">
                Aucun code acheté
              </div>
            ) : (
              <ul className="space-y-2 max-h-96 overflow-y-auto">
                {codes.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`admin-code-${c.code}`}
                    className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono-stat font-bold text-cyan-300 tracking-widest">{c.code}</span>
                      <button onClick={() => copy(c.code)} className="text-white/30 hover:text-white">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span
                        className={`px-2 py-0.5 rounded-full font-mono-stat uppercase tracking-widest ${
                          c.status === "consumed"
                            ? "bg-amber-400/15 text-amber-300"
                            : "bg-emerald-400/15 text-emerald-300"
                        }`}
                      >
                        {c.status === "consumed" ? "Utilisé" : "Inutilisé"}
                      </span>
                      <span className="text-white/40 font-mono-stat">
                        {c.amount_paid?.toFixed(2)} {c.currency?.toUpperCase()}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Leaderboard */}
          <div className="panel rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-300" />
                <span className="font-display font-bold text-lg">Classement</span>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/40">
                {leaderboard.revealed ? "Révélé" : "Visible admin uniquement"}
              </span>
            </div>
            {leaderboard.rows.length === 0 ? (
              <div className="py-8 text-center text-white/40 text-sm font-mono-stat uppercase tracking-widest">
                Aucun joueur encore
              </div>
            ) : (
              <ul className="space-y-2 max-h-96 overflow-y-auto">
                {leaderboard.rows.map((p, i) => (
                  <li
                    key={`${p.nickname}-${i}`}
                    data-testid={`admin-leaderboard-row-${i}`}
                    className={`flex flex-col gap-1 px-3 py-2.5 rounded-xl border ${
                      p.is_winner
                        ? "bg-amber-400/15 border-amber-400/40"
                        : "bg-white/5 border-white/10"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-mono-stat font-bold text-white/50 w-7">#{i + 1}</span>
                        <span className="font-display font-bold">{p.nickname}</span>
                        {p.is_winner && (
                          <span className="text-[10px] uppercase tracking-widest font-mono-stat text-amber-300">
                            gagnant
                          </span>
                        )}
                        {p.completed && (
                          <span className="text-[10px] uppercase tracking-widest font-mono-stat text-emerald-300">
                            terminé
                          </span>
                        )}
                      </div>
                      <span className="font-mono-stat font-bold text-cyan-300">{p.total_score}</span>
                    </div>
                    {p.is_winner && (p.payer_email || p.iban) && (
                      <div className="text-xs font-mono-stat space-y-0.5 pt-1 border-t border-white/10">
                        {p.payer_email && (
                          <div className="flex items-center justify-between text-white/70">
                            <span className="text-white/40 uppercase tracking-widest text-[9px]">Email</span>
                            <div className="flex items-center gap-2">
                              <span className="select-all">{p.payer_email}</span>
                              <a
                                data-testid={`admin-mailto-${i}`}
                                href={`mailto:${p.payer_email}?subject=${encodeURIComponent("OtraQuizz — tes gains sur " + (challenge?.title || ""))}&body=${encodeURIComponent("Bravo " + (p.nickname || "") + " !\n\nTu termines à la place #" + (i + 1) + " du défi \"" + (challenge?.title || "") + "\".\n\nPour recevoir ta part de la cagnotte, merci de me confirmer ton IBAN et le nom du titulaire du compte.\n\nÀ très vite,\nOtraQuizz")}`}
                                className="px-2 py-0.5 rounded-md bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/30 transition-all text-[10px] uppercase tracking-wider no-underline"
                              >
                                ✉ Envoyer
                              </a>
                            </div>
                          </div>
                        )}
                        {p.iban ? (
                          <>
                            <div className="flex items-center justify-between text-white/70">
                              <span className="text-white/40 uppercase tracking-widest text-[9px]">Titulaire</span>
                              <span className="select-all">{p.iban_holder_name}</span>
                            </div>
                            <div className="flex items-center justify-between text-amber-200">
                              <span className="text-white/40 uppercase tracking-widest text-[9px]">IBAN</span>
                              <span className="select-all font-bold tracking-widest">{p.iban}</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-white/30 italic">IBAN pas encore réclamé</div>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
